import React, { useEffect, useMemo } from 'react'
import { IonCol, IonGrid, IonItem, IonItemDivider, IonLabel, IonRow, IonToggle } from '@ionic/react'
import TimesRow from '../../features/times/timesRow/TimesRow.component'
import { useDispatch, useSelector } from 'react-redux'
import { IProcedureHeader } from './Procedure.component'
import SingleSelect from '../singleSelect/SingleSelect.component'
import { getProcedureStateSelector } from './selectors'
import './Procedure.component.css'
import { useTranslation } from 'react-i18next'
import NumberPad from '../numberPad/NumberPad.component'
import { IOption, IPNNVOption } from '../../common/common.types'
import { IPHIComponent } from '../phi/phi.component'
import { getTimesSelector } from '../../features/times/selectors'
import { isValidZollNowDate, isValidZollTime } from '../../utilities/dateTime.util'
import { showAlert } from '../alert/store/alert.actions'
import { getPTADateTimeErrorMessage, isProcDateTimePTA } from '../../utilities/procedures.util'
import { IServiceConfig } from '../../features/configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../features/configurations/selectors/serviceConfig.selector'
import { syncPtaWithParentVitals } from '../../features/timeline/store/timeline.actions'
import { getSceneDescriptionOptionsSelector } from '../../features/options/selectors'

export interface IProcedureHeaderComponentProps extends IProcedureHeader, IPHIComponent {
  procedureType: string
  hideAttemptField?: boolean
  hideSuccessfulAttempt?: boolean
  disablePta?: boolean
}

const ProcedureHeaderComponent: React.FC<IProcedureHeaderComponentProps> = (props: IProcedureHeaderComponentProps) => {
  const {
    procedureTime,
    procedureDate,
    procedureType,
    assessedBy,
    successfulAttempt,
    pta,
    attempts,
    isPHI,
    hideAttemptField,
    rolePerforming,
    rolePerformingPNNV,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
    hideSuccessfulAttempt,
    isEkgImport,
    disablePta,
  } = props
  const dispatch = useDispatch()
  const procedure = useSelector(getProcedureStateSelector)
  const { assessedByOptions, rolePerformingOptions } = procedure
  const { firstOnSceneOptions } = useSelector(getSceneDescriptionOptionsSelector)

  const { atPatientDate, atPatientTime, onSceneDate, onSceneTime } = useSelector(getTimesSelector)
  const { DISPLAY_SECONDS }: IServiceConfig = useSelector(getServiceConfigSelector)
  const displaySeconds = !!DISPLAY_SECONDS

  const { t } = useTranslation()

  const isPtaLocked = !!isEkgImport || !!disablePta

  const checkAssessedBy = useMemo(() => {
    if (assessedBy) {
      const assessedByIndex = assessedByOptions.findIndex((option) => option.id === assessedBy.id)
      if (assessedByIndex < 0) {
        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }, [assessedBy])

  const runPTAValidation = () => {
    const errMsg = getPTADateTimeErrorMessage(
      atPatientDate,
      atPatientTime,
      onSceneDate,
      onSceneTime,
      procedureDate,
      procedureTime,
      displaySeconds
    )
    if (errMsg) {
      dispatch(showAlert({ message: errMsg }))
      dispatch({
        type: `${procedureType}/SET_PTA`,
        pta: false,
      })
      dispatch(syncPtaWithParentVitals(false))
    }
  }

  useEffect(() => {
    if (isPtaLocked) return
    if (pta && isValidZollNowDate(procedureDate)) {
      runPTAValidation()
    } else if (!pta && isValidZollNowDate(procedureDate) && isValidZollTime(procedureTime)) {
      if (
        isProcDateTimePTA(
          atPatientDate,
          atPatientTime,
          onSceneDate,
          onSceneTime,
          procedureDate,
          procedureTime,
          displaySeconds
        )
      ) {
        dispatch({
          type: `${procedureType}/SET_PTA`,
          pta: true,
        })
        dispatch(syncPtaWithParentVitals(true))
      }
    }
  }, [procedureDate, procedureTime, pta, isPtaLocked])

  const onPTAClick = () => {
    if (isPtaLocked) return
    const isPTA = !pta
    dispatch({
      type: `${procedureType}/SET_PTA`,
      pta: isPTA,
    })
    dispatch(syncPtaWithParentVitals(isPTA))
  }

  return (
    <>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          Add Procedure details
        </IonLabel>
      </IonItemDivider>

      <IonGrid className='ion-no-padding'>
        <IonRow className='picklist'>
          <IonCol>
            <TimesRow
              elementId='procedure-dateTime'
              header={t('procedure.header.dateTime')}
              time={procedureTime}
              date={procedureDate}
              timeAction={`${procedureType}/SET_TIME`}
              dateAction={`${procedureType}/SET_DATE`}
              currentDateTimeAction={`${procedureType}/SET_DATE_TIME`}
              disabled={!!isEkgImport}
              subHeader={isEkgImport ? t('times.fromVitals') : ''}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              isPHI={isPHI}
              dataTestID='AssessedBy'
              selectedOption={assessedBy}
              listHeader={t('procedure.header.assessedBy')}
              label={t('procedure.header.assessedBy')}
              options={assessedByOptions}
              action={(option: IOption) => dispatch({ type: `${procedureType}/SET_ASSESSED_BY`, assessedBy: option })}
            />
            {checkAssessedBy && <IonLabel color='danger'>{t('procedure.header.AssessedByError')}</IonLabel>}
          </IonCol>
        </IonRow>
        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              isPHI={isPHI}
              dataTestID='RolePerforming'
              selectedOption={rolePerforming}
              listHeader='Role Performing'
              label='Role Performing'
              options={rolePerformingOptions}
              action={(option: IOption) =>
                dispatch({ type: `${procedureType}/SET_ROLE_PERFORMING`, rolePerforming: option })
              }
              selectedPNNVOption={rolePerformingPNNV}
              pnnvAction={(option: IPNNVOption) =>
                dispatch({ type: `${procedureType}/SET_ROLE_PERFORMING_PNNV`, rolePerformingPNNV: option })
              }
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='ProcedurePerformedByAnotherUnit'
              selectedOption={procedurePerformedByAnotherUnit}
              listHeader='Procedure Performed By Another Unit'
              label='Procedure Performed By Another Unit'
              options={firstOnSceneOptions}
              action={(option: IOption) =>
                dispatch({
                  type: `${procedureType}/SET_PROCEDURE_PERFORMED_BY_ANOTHER_UNIT`,
                  procedurePerformedByAnotherUnit: option,
                })
              }
              selectedPNNVOption={procedurePerformedByAnotherUnitPNNV}
              pnnvAction={(option: IPNNVOption) =>
                dispatch({
                  type: `${procedureType}/SET_PROCEDURE_PERFORMED_BY_ANOTHER_UNIT_PNNV`,
                  procedurePerformedByAnotherUnitPNNV: option,
                })
              }
            />
          </IonCol>
        </IonRow>
      </IonGrid>

      {
        <>
          <IonItemDivider color='light' />
          {(!hideAttemptField || !hideSuccessfulAttempt) && (
            <>
              <IonRow id='Attempts'>
                {!hideAttemptField && (
                  <IonCol className='picklist'>
                    <NumberPad
                      dataTestId='Attempts'
                      elementId='Attempts'
                      values={attempts ? attempts.split('') : []}
                      label={t('procedure.header.attempts')}
                      onNumPadClick={(numbers) =>
                        dispatch({
                          type: `${procedureType}/SET_ATTEMPTS`,
                          attempts: numbers.length ? numbers.join('') : '',
                        })
                      }
                    />
                  </IonCol>
                )}

                {!hideSuccessfulAttempt && (
                  <IonCol>
                    <IonItem lines='none'>
                      <IonLabel>{t('procedure.header.successfulAttempt')}</IonLabel>
                      <IonToggle
                        slot='start'
                        name='successfulAttempt'
                        checked={successfulAttempt}
                        onIonChange={(e) =>
                          dispatch({
                            type: `${procedureType}/SET_SUCCESSFUL_ATTEMPT`,
                            successfulAttempt: e.detail.checked,
                          })
                        }
                        color='zoll-blue-secondary'
                      />
                    </IonItem>
                  </IonCol>
                )}
              </IonRow>

              {(!hideSuccessfulAttempt || !hideAttemptField) && <hr className='field-divider' />}
            </>
          )}
          <IonRow>
            <IonCol className='picklist'>
              <IonItem className='toggle-item' lines='none'>
                <IonLabel>{t('procedure.header.pta')}</IonLabel>
                <IonToggle
                  slot='start'
                  name='pta'
                  checked={pta}
                  onClick={onPTAClick}
                  disabled={isPtaLocked}
                  color='zoll-blue-secondary'
                />
              </IonItem>
            </IonCol>
          </IonRow>
        </>
      }

      <IonItemDivider color='light' className='bottom-item-divider' />
    </>
  )
}

export default ProcedureHeaderComponent
